/**
 * Utility functions for handling video in the PDPVideo component
 */

/**
 * Safely parse JSON with error handling
 * @param {HTMLElement} element - DOM element containing JSON
 * @param {string} errorPrefix - Prefix for error messages
 * @returns {Object|null} - Parsed JSON object or null if error
 */
export function parseJSON(element, errorPrefix) {
  if (!element) return null;
  try {
    return JSON.parse(element.textContent);
  } catch (e) {
    console.error(`🎬 ${errorPrefix}:`, e);
    return null;
  }
}

/**
 * Create a debounced function
 * @param {Function} func - Function to debounce
 * @param {number} wait - Milliseconds to wait between calls
 * @returns {Function} - Debounced function
 */
export function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

/**
 * Safely play a video with error handling
 * @param {HTMLVideoElement} video - Video element to play
 * @returns {Promise} - Promise that resolves when video plays or rejects if error
 */
export function playVideo(video) {
  if (!video) return Promise.reject(new Error('No video element provided'));
  return video.play().catch((err) => console.warn('🎬 Could not play video:', err));
}

/**
 * Get the metafield filename for a given barcode
 * @returns {string|null} - Filename from metafield or null
 */
export function getMetafieldFilename() {
  // Implementation would depend on how metafields are accessed
  // This is a placeholder function
  return null;
}

/**
 * Get video URL based on video object properties
 * @param {Object} video - Video object with source information
 * @param {boolean} preferThumbnail - Whether to return thumbnail URL instead of video
 * @returns {string|null} - URL for the video or null if not available
 */
export function getVideoUrl(video, preferThumbnail = false) {
  if (!video) return null;

  // For thumbnails, try to get preview image
  if (preferThumbnail) {
    // First check for preview_image property from JSON data
    if (video.preview_image) {
      return video.preview_image.startsWith('/') ? video.preview_image : `/${video.preview_image}`;
    }

    // Then check sources array (Shopify admin format)
    if (video.sources && Array.isArray(video.sources)) {
      const previewImage = video.sources.find(
        (source) => source.format === 'preview_image' || source.format === 'thumbnail_image',
      );

      if (previewImage && previewImage.url) {
        return previewImage.url;
      }
    }

    // Fallback to thumbnail from video ID
    if (video.id) {
      return `https://cdn.shopify.com/videos/c/vp/${video.id}/thumbnail.jpg`;
    }
  }

  // For video slides, use HD version when available
  if (
    !preferThumbnail &&
    video.sources &&
    Array.isArray(video.sources) &&
    video.sources.length > 1 &&
    video.sources[1] &&
    video.sources[1].url
  ) {
    return video.sources[1].url;
  }

  // Priority-based URL resolution for video sources
  if (video.url && (video.url.includes('cdn.shopify.com/videos') || video.url.includes('cdn.shopify.com/s/files'))) {
    return video.url;
  }

  if (video.file_url) {
    return video.file_url.startsWith('/') ? video.file_url : `/${video.file_url}`;
  }

  if (video.url) {
    return video.url.startsWith('/') ? video.url : `/${video.url}`;
  }

  if (video.filename) {
    let filename = video.filename.toLowerCase();
    const supportedFormats = ['.mp4', '.mov', '.webm'];
    const hasVideoExtension = supportedFormats.some((ext) => filename.endsWith(ext));
    if (!hasVideoExtension) {
      filename = `${video.filename}.mp4`; // fallback to mp4
    } else {
      filename = video.filename;
    }
    return `/cdn/shop/files/${filename}`;
  }

  return null;
}
